import React,{FC, useEffect, useState, useMemo} from "react";
import { PageTitle } from "../../../../_metronic/layout/core";
import {useIntl} from 'react-intl'
import { Card, Row, Col, Form, Button, Dropdown, Image } from "react-bootstrap";
import { useFTAImpactContext } from '../../../contexts/fta-impact/FTAImpactContext';
import { formatPercentageNumber, getNameByISO3, shortenLargeNumber } from "../../../libs/misc";
import { CSVLink } from 'react-csv'
import InputOutputFormTable from "./components/InputOutputFormTable";
import InputOutputResultTable from "./components/InputOutputResultTable";
import { runSimulation } from "./core/_requests";
import useAxiosPrivate from "../../../libs/useAxiosPrivate";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { useAuth } from "../../../modules/auth";
import { Toast } from "../../../../ui-kit";

const InputOutputPage: FC =  () => {
  const intl = useIntl()
  const axiosPrivate = useAxiosPrivate()
  const {year, partner} = useFTAImpactContext()
  const [simulationInput, setSimulationInput] = useState<any>(null)
  const [simulationResult, setSimulationResult] = useState<any>(null)
  const [simulationSummary, setSimulationSummary] = useState<any>(null)
  const [showFormTable, setShowFormTable] = useState<boolean>(true)
  const [showResultTable, setShowResultTable] = useState<boolean>(false)
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  const reporterNameUser = getNameByISO3(reporterISO3User);

  const handleSimulationInputChange = (input: any) => {
    setSimulationInput(input)
  }

  const handleSubmitSimulation = () =>{
    setShowFormTable(false)
    setShowResultTable(true)
    setSimulationResult(null)

    runSimulation(axiosPrivate,{
      data:simulationInput,
      rep:reporterISO3User,
      year:'2020'
    }).then((res) => {
      const resData = res.data.data
      setSimulationResult(resData.rows)
      setSimulationSummary(resData.summary)
    }).catch((err) => {
      Toast('Error', 'danger', 'Error occurred while running simulation.')
      console.error(err)
    })
  }

  const handleShowFormTable = () => {
    setShowFormTable(true)
    setShowResultTable(false)
  }

    const generateCSVData = () =>{
    if(!simulationResult || simulationResult.length===0){
      return []
    }

    return simulationResult.map(row=>{
      return {
        oecd_code: row.oecd_code,
        hs_code_concordance: row.hs_code_concordance,
        description: row.desc,
        export_to_world: row.export_wld,
        export_mirror_data: row.export_mir_wld,
        import: row.import,
        domestic_export_change: row.domestic_export_change,
        demand_original: row.demand_original,
        demand_new: row.demand_new,
        output_original: row.output_original,
        output_new: row.output_new,
        delta_output: row.delta_output,
      }
    })
  }

  const handleBtnExportCSV = () =>{
    if(!simulationResult || simulationResult.length === 0){
      return false
    }
  }
  
  return (
    <>
    <PageTitle breadcrumbs={[{
        title: 'Home',
        path: '/',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },]}>
        {intl.formatMessage({id: 'MENU.INPUT_OUTPUT_ANALYSIS'})}
      </PageTitle>
      <Row className="mb-5">
        <Col>
          <Card className="card-custom card-border">
            <Card.Body className="d-flex justify-content-around align-items-center">
              <Image src="media/tina/img-result-ntms.jpg" thumbnail width={110}/>
              <div  className="ms-5 d-flex flex-column justify-content-center">
                <h3 className="text-primary">Input/Output Analysis
                  <span className="text-gray-700">
                    <span className="fa-solid fa-angles-right px-1"></span>
                    trace intersectoral linkages of your RTA simulation
                  </span> 
                </h3>
                <div className="text-gray-700">
                  <p>
                    Use this simulation tool to evaluate the economy-wide effects of your RTA impact simulation by tracing intersectoral linkages using {reporterNameUser}’s input-output tables.
                  </p>
                  <p>
                    Input-output (IO) analysis complements Partial Equilibrium (PE) impact assessment through modelling sectoral interlinkages, albeit at a more aggregated level. To adjust for discrepancies in two datasets, while keeping the IO tables balanced, PE simulation results are first aggregated by <a href="https://unstats.un.org/unsd/classifications/Family/Detail/27">ISIC Rev.4</a> sectors (column: Total export change).
                  </p>
                  <p>
                    Next, the ratio of these values and {reporterNameUser} total exports (based on PE dataset) are used to create a multiplier. This multiplier is then used to calculate new {reporterNameUser}'s total exports. The new total exports are subsequently used to derive new total demand and respective sectors’ change in output.
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>  
        </Col>
      </Row>
        <Row className={clsx(
          'table-responsive table-form-input-output',
          {'d-none': !showFormTable})}>
          <Col>
            <Card>
              <Card.Header className="card-header-stretch">
                <Card.Title className="w-100 d-flex justify-content-between align-items-center">
                  <div>
                    <h3>
                      <i className="fa-solid fa-barcode me-5"></i>
                    </h3>
                  </div>
                  <div>
                    <small>
                      Trade data: {year}
                    </small>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <InputOutputFormTable 
                  key="tableFormInputOutput" 
                  onSimulationInputChange={handleSimulationInputChange}
                  onSubmitSimulation={handleSubmitSimulation}/>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className={clsx('table-responsive table-result-input-output mt-5', {
          'd-none': !showResultTable,
        })}>
          <Col>
            <Card>
              <Card.Header className="card-header-stretch">
                <Card.Title className="w-100 d-flex justify-content-between align-items-center">
                  <div className='text-gray-800 fw-normal fs-7 d-flex align-items-center justify-content-center' style={{width:'80%', lineHeight: '1.5'}}>
                    <i className="fa-solid fa-chart-line me-5"></i>
                    <div>
                      The following table shows the IO simulation results for {reporterNameUser} based on your earlier PE simulation results. Total export change was <b>${shortenLargeNumber(simulationSummary?.total_export_change_usd)} ({formatPercentageNumber(simulationSummary?.total_export_change_percent)}% of total exports)</b> in PE analysis, which results in <b>${shortenLargeNumber(simulationSummary?.total_output_change_usd*1000000)} ({formatPercentageNumber(simulationSummary?.total_output_change_percent)}%)</b> change in {reporterNameUser}'s' total output. See the following table for details.
                    </div>
                  </div>
                  <div>
                    <small>
                      Trade data: {year}
                    </small>
                    <div className="btn-group ms-3">
                      <button type="button" className="btn btn-outline btn-sm dropdown-toggle text-gray-700" data-bs-toggle="dropdown" aria-expanded="false" disabled={!simulationResult} >
                        Action
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <CSVLink 
                            data={generateCSVData()||[]} 
                            filename={`input-output-simulation-result-${partner}-${year}.csv`}
                            onClick={handleBtnExportCSV}
                            className="dropdown-item"
                            >
                              <i className="bi bi-download me-3"></i>
                              Export to CSV
                          </CSVLink >
                        </li>
                        <li>
                          <Link 
                            className="fw-normal dropdown-item" 
                            to={""}
                            onClick={handleShowFormTable}>
                              <i className="fa-solid fa-arrow-left me-3"></i>
                              Change the Impact Parameter
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <InputOutputResultTable resultData={simulationResult}/>
              </Card.Body>
              <Card.Footer>
                <small>Source:&nbsp;
                  <a href="https://www.oecd.org/en/data/datasets/input-output-tables.html">
                    OECD Input-Output tables
                  </a>,
                  <a href="https://comtrade.un.org/">
                    &nbsp;Comtrade
                  </a>,
                    &nbsp;Partial Equilibrium simulation results
                </small>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      
    </>
  )
}

export default InputOutputPage